import { SOCKET_URL } from 'constants/index';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client'; // Use `import type` for type-only imports
import { create } from 'zustand';
import useChatStore, { setNewMessageAlert } from './chat';
import { devtools } from 'zustand/middleware';
import { logMessageReceived, logMessageSent } from 'services';

// Type Definitions
interface SocketState {
	socket: Socket | null;
	token: string | null;
	connect: () => void;
	disconnect: () => void;
	setToken: (token: string) => void;
	sendMessage: (values: any) => void;
	saveSocketId: (socketId: string) => void;
	userOnline: (userId: string) => void;
	newMessage: (message: any) => void;
	newChatListMessage: (message: any) => void;
	chatList: (params: {
		page: number;
		limit: number;
		order: string;
		keyword: string;
		headers: { Authorization: string };
	}) => void;
	getAllMessagesById: (message: any) => void;
	seenMessage: (chatId: string) => void;
	startTyping: (userId: string) => void;
	endTyping: (userId: string) => void;
	blockUser: (userId: string) => void;
	unblockUser: (userId: string) => void;
	resetState: () => void; // New reset function
}

// Initialize token from secureStorage
import { secureStorage } from 'utils/secureStorage';
const storageData = secureStorage.getItem('auth') || {};
const tokenFromStorage = storageData?.state?.userInfo?.token ?? '';

const initialState: SocketState = {
	socket: null,
	token: tokenFromStorage,
	connect: () => {}, // Placeholder for connect function
	disconnect: () => {}, // Placeholder for disconnect function
	setToken: () => {}, // Placeholder for setToken function
	sendMessage: () => {}, // Placeholder for sendMessage function
	saveSocketId: () => {}, // Placeholder for saveSocketId function
	userOnline: () => {}, // Placeholder for userOnline function
	newMessage: () => {}, // Placeholder for newMessage function
	newChatListMessage: () => {}, // Placeholder for newChatListMessage function
	chatList: () => {}, // Placeholder for chatList function
	getAllMessagesById: () => {}, // Placeholder for getAllMessagesById function
	seenMessage: () => {}, // Placeholder for seenMessage function
	startTyping: () => {}, // Placeholder for startTyping function
	endTyping: () => {}, // Placeholder for endTyping function
	blockUser: () => {}, // Placeholder for blockUser function
	unblockUser: () => {}, // Placeholder for unblockUser function
	resetState: () => {}, // Placeholder for resetState function
};

const socketStore = (set: any, get: any) => ({
	...initialState,

	connect: () => {
		const currentState = get();
		// Prevent multiple connections
		if (currentState.socket?.connected) {
			console.log('Socket already connected');
			return;
		}

		// Disconnect existing socket if any
		if (currentState.socket) {
			currentState.socket.disconnect();
		}

		const newSocket = io(SOCKET_URL);
		set({ socket: newSocket });

		// Remove existing listeners to prevent duplicates
		newSocket.off('connect');
		newSocket.off('newMessage');
		newSocket.off('chatList');
		newSocket.off('startTyping');
		newSocket.off('endTyping');
		newSocket.off('blockUserListener');
		newSocket.off('unblockUserListener');

		newSocket.on('connect', () => {
			const socketId = newSocket.id;
			const token = get().token;
			setTimeout(() => {
				if (socketId && token) {
					get().saveSocketId(socketId);
					get().chatList({ order: 'DESC', keyword: '' });
				}
			}, 200);
		});

		newSocket.on('newMessage', (message: any) => {
			const { chatMessages, setChatMessages, roomId, chatListKeyword } = useChatStore.getState();
			logMessageReceived(message?.roomId);
			if (message?.receiverId !== message?.chatUserId) {
				setNewMessageAlert(true);
				if (Number(message?.roomId) === Number(roomId)) get().seenMessage(roomId);
			}

			if (roomId === Number(message?.roomId)) {
				const updatedMessages: any = [
					message,
					...(Array.isArray(chatMessages) ? chatMessages : []),
				];

				setChatMessages(updatedMessages);
			}
			get().chatList({ order: 'DESC', keyword: chatListKeyword });
		});

		// Other event listeners
		newSocket.on('chatList', (list: any) => {
			const { setUserList } = useChatStore.getState();
			setUserList(list);
		});
		newSocket.on('startTyping', (data: any) => {
			const { setIsTypingRooms } = useChatStore.getState();
			setIsTypingRooms(data?.roomId);
		});
		newSocket.on('endTyping', (data: any) => {
			const { removeIsTypingRoom } = useChatStore.getState();
			removeIsTypingRoom(data?.roomId);
		});
		newSocket.on('blockUserListener', (data: any) => {
			const { chatListKeyword, refetchChatDetailsQuery, roomId } = useChatStore.getState();
			get().chatList({ order: 'DESC', keyword: chatListKeyword });
			if (Number(roomId) === Number(data?.roomId)) refetchChatDetailsQuery();
		});
		newSocket.on('unblockUserListener', (data: any) => {
			const { chatListKeyword, refetchChatDetailsQuery, roomId } = useChatStore.getState();
			get().chatList({ order: 'DESC', keyword: chatListKeyword });
			if (Number(roomId) === Number(data?.roomId)) refetchChatDetailsQuery();
		});
	},

	disconnect: () => {
		set((state: any) => {
			state.socket?.off();
			state.socket?.disconnect();
			return { socket: null };
		});
	},

	setToken: (token: string) => {
		set({ token });
	},

	saveSocketId: (socketId: string) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				socket.emit('saveSocketId', { socketId, headers: { Authorization: `Bearer ${token}` } });
			}
			return state;
		});
	},

	sendMessage: (values: any) => {
		set((state: any) => {
			const { socket, token } = state;
			const { message, receiverId, attachmentType } = values;
			logMessageSent(message?.roomId || receiverId);
			if (socket && token && message && receiverId && attachmentType) {
				socket.emit('sendMessage', {
					message,
					receiverId,
					attachmentType,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	chatList: (params: { page: number; limit: number; order: string; keyword: string }) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				socket.emit('chatList', {
					...params,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	getAllMessagesById: (params: { roomId: string; lastMessageId: number }) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				socket.emit('getAllMessagesById', {
					...params,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	startTyping: (roomId: string) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				socket.emit('startTyping', {
					roomId,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	endTyping: (roomId: string) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				socket.emit('endTyping', {
					roomId,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	seenMessage: (roomId: string) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				socket.emit('seenMessage', {
					roomId,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	blockUser: (blockUserId: number) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				const { roomId } = useChatStore.getState();
				socket.emit('blockUser', {
					roomId,
					blockUserId,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	unblockUser: (blockUserId: number) => {
		set((state: any) => {
			const { socket, token } = state;
			if (socket && token) {
				const { roomId } = useChatStore.getState();
				socket.emit('unblockUser', {
					roomId,
					blockUserId,
					headers: { Authorization: `Bearer ${token}` },
				});
			}
			return state;
		});
	},

	// Reset state function
	resetState: () => {
		set({ ...initialState });
	},
});

const useSocketStore = create<SocketState>(devtools(socketStore));

export const {
	connect,
	disconnect,
	sendMessage,
	seenMessage,
	saveSocketId,
	setToken,
	startTyping,
	endTyping,
	blockUser,
	unblockUser,
	resetState, // Export the reset function
} = useSocketStore.getState();

export default useSocketStore;
